/**
 * Location defaults admin API module — the labor rate and auto-approval
 * amount as an account-default / location-value family (location_defaults).
 *
 *   GET|PATCH /accounts/{a}/location-defaults                       (PATCH: accounts:update | account:admin)
 *   GET|PATCH /accounts/{a}/locations/{l}/location-defaults         (PATCH: locations:update)
 *
 * Envelope: { account_id, location_id|null, labor_rate, auto_approval_amount
 * (decimal strings, null = unset; 0/null auto-approval = disabled), sources,
 * locked, overrides|null, account: { …cells, locked_fields } }.
 * PATCH is tri-state (absent = untouched, null = clear, value = set); the
 * account PATCH may also send locked_fields ([] unlocks all). A location
 * PATCH on a locked key is refused with 409 POLICY_FIELD_LOCKED. The legacy
 * PUT /locations/{id} with those fields still works (409 if locked).
 */
import { webApiClient } from '../apiClient';
import { dedup } from './helpers';
import { toPolicyApiError } from './policyErrors';
import type { LocationDefaultsAccountPatch, LocationDefaultsEnvelope, LocationDefaultsPatch } from '../../components/policy/policyEnvelope';

const accountPath = (accountId: string) => `/accounts/${accountId}/location-defaults`;
const locationPath = (accountId: string, locationId: string) => `/accounts/${accountId}/locations/${locationId}/location-defaults`;

/** Decimal cells may arrive as numbers from an older server; keep them strings. */
function normalize(raw: Record<string, any> | undefined | null): LocationDefaultsEnvelope {
  const cell = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
  const row = (r: Record<string, any> | null | undefined) =>
    r
      ? {
          ...(r.labor_rate !== undefined ? { labor_rate: cell(r.labor_rate) } : {}),
          ...(r.auto_approval_amount !== undefined ? { auto_approval_amount: cell(r.auto_approval_amount) } : {}),
          ...(r.locked_fields !== undefined ? { locked_fields: Array.isArray(r.locked_fields) ? r.locked_fields : [] } : {}),
        }
      : null;
  const d = raw ?? {};
  return {
    account_id: d.account_id ?? '',
    location_id: d.location_id ?? null,
    labor_rate: cell(d.labor_rate),
    auto_approval_amount: cell(d.auto_approval_amount),
    sources: d.sources ?? {},
    locked: Array.isArray(d.locked) ? d.locked : [],
    overrides: row(d.overrides),
    account: row(d.account),
  };
}

export async function getAccountLocationDefaults(accountId: string): Promise<LocationDefaultsEnvelope> {
  return dedup(`getAccountLocationDefaults:${accountId}`, async () => {
    const res = await webApiClient.get<Record<string, any>>(accountPath(accountId));
    if (!res.success) throw toPolicyApiError(res, 'Failed to load the location defaults');
    return normalize(res.data);
  });
}

export async function updateAccountLocationDefaults(
  accountId: string,
  patch: LocationDefaultsAccountPatch
): Promise<LocationDefaultsEnvelope> {
  const res = await webApiClient.patch<Record<string, any>>(accountPath(accountId), patch);
  if (!res.success) throw toPolicyApiError(res, 'Failed to update the location defaults');
  return normalize(res.data);
}

export async function getLocationDefaults(accountId: string, locationId: string): Promise<LocationDefaultsEnvelope> {
  return dedup(`getLocationDefaults:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<Record<string, any>>(locationPath(accountId, locationId));
    if (!res.success) throw toPolicyApiError(res, 'Failed to load the location defaults');
    return normalize(res.data);
  });
}

export async function updateLocationDefaults(
  accountId: string,
  locationId: string,
  patch: LocationDefaultsPatch
): Promise<LocationDefaultsEnvelope> {
  const res = await webApiClient.patch<Record<string, any>>(locationPath(accountId, locationId), patch);
  if (!res.success) throw toPolicyApiError(res, 'Failed to update the location defaults');
  return normalize(res.data);
}

export const locationDefaultsAdmin = {
  getAccountLocationDefaults,
  updateAccountLocationDefaults,
  getLocationDefaults,
  updateLocationDefaults,
};
